import {
	Component,
	computed,
	ContentChild,
	EventEmitter,
	inject,
	Input,
	AfterViewInit,
	Output,
	QueryList,
	SimpleChanges,
	TemplateRef,
	ViewChild,
	ViewChildren,
} from '@angular/core';
import { mspTableHeaderI } from '../msp-table/msp-table.component';
import { CommonModule } from '@angular/common';
import { faAngleRight, faDownload, faFileCircleXmark, faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { SquarePostLoadingEffectComponent } from '../square-post-loading-effect/square-post-loading-effect.component';
import { MspTableSortDropdownComponent } from './components/msp-table-sort-dropdown/msp-table-sort-dropdown.component';
import { contextMenuOptions, ContextMenuService } from 'src/app/services/context-menu/context-menu.service';
import { DateService } from 'src/app/services/date/date.service';
import { LabelsService } from 'src/app/util/services/labels/labels.service';
import { LabelsInterface } from 'src/app/util/services/labels/data/label.inteface';
import { TooltipDirective } from 'src/app/util/directives/tooltip/tooltip.directive';
import { genericFormI } from 'src/app/util/interfaces/genericFormRMK';
import { MspTableCustomizeHeadersComponent } from './components/msp-table-customize-headers/msp-table-customize-headers.component';
import { RouterModule } from '@angular/router';
import { mspTableSort } from './components/msp-table.interfaces';

@Component({
	selector: 'msp-table-rmk',
	standalone: true,
	imports: [
		CommonModule,
		FontAwesomeModule,
		SquarePostLoadingEffectComponent,
		MspTableSortDropdownComponent,
		MspTableCustomizeHeadersComponent,
		TooltipDirective,
		RouterModule,
	],
	templateUrl: './msp-table-rmk.component.html',
	styleUrls: ['./msp-table-rmk.component.scss'],
})
export class MspTableRmkComponent implements AfterViewInit {
	private readonly contextMenuService = inject(ContextMenuService);
	private readonly labelsService = inject(LabelsService);
	protected readonly dateService = inject(DateService);
	@ViewChildren('sortDropdown') sortDropdown!: QueryList<MspTableSortDropdownComponent>;
	@ViewChild('customizeHeadersComponent') customizeHeadersComponent!: MspTableCustomizeHeadersComponent;

	icons = {
		faSortDown: faSortDown,
		faSortUp: faSortUp,
		faSort: faSort,
		faAngleRight: faAngleRight,
		faFileCircleXmark: faFileCircleXmark,
		faDownload: faDownload,
	};
	labels: LabelsInterface = {};

	@Input() headers: mspTableHeaderI[];
	@Output() getHeaders = new EventEmitter<mspTableHeaderI[]>();
	@Output() getNewHeaders = new EventEmitter<mspTableHeaderI[]>();

	@Input() data: any[];

	@Input() enableSorting = false;
	@Input() sort: mspTableSort;
	@Output() getSort = new EventEmitter<mspTableSort>();
	allowedSortTypes = ['text', 'date', 'multi-select'];

	@Input() enableSearching = false;
	searchData: { [k: string]: { id: string; values: any[] } };
	@Output() getSearch = new EventEmitter<{ id: string; values: any[] }>();
	@Output() cleanSearch = new EventEmitter<null>();

	@Input() enableSelecting = false;
	@Input() enableLineSelection = false;
	selectedRowIndex: number[] = [];
	@Output() getSelectedRow = new EventEmitter<any[]>();

	@Input() enableExpanding = false;
	expandedRowIndex: number[] = [];
	@ContentChild('expanded') expandedTemplate?: TemplateRef<any>;

	@Input() contextMenuOptions: contextMenuOptions[];
	@Output() getContextMenuData = new EventEmitter<any>();

	@Input() enableContextMenu = false;
	optionsContextMenuOptions: contextMenuOptions[] = [];
	@Input() selectContextMenuRow = false;

	@Input() enableExport = false;
	@Output() exportTable = new EventEmitter<void>();

	@Input() enableCustomizeHeaders = false;
	@Input() availableHeaders: genericFormI['items'][] = [];
	@Input() defaultHeaders: string[] = [];
	@Output() saveHeaders = new EventEmitter<genericFormI['rawValues'][]>();

	@Input() customButtons: { text: string; url: string | any[] }[];

	@Input() onLineClick: (data: any) => void;
	@Input() onLineRouterLink: string;
	// Mantém as células com link na mesma linha do grid, garantindo que as cores de hover e seleção cubram toda a linha.
	// É opcional para preservar o layout existente das demais tabelas.
	@Input() flattenLineLinks = false;
	fnOnLineRouterLink: (data: any) => string = (data) => {
		if (!this.onLineRouterLink) return null;
		let link = this.onLineRouterLink;
		for (const key of Object.keys(data)) {
			link = link.replace(`:${key}`, data[key]);
		}
		return link;
	};

	@ContentChild('noData') noDataTemplate?: TemplateRef<any>;
	@Input() containerStyle: { [key: string]: string } = {};

	@Input() isLoading = false;

	constructor() {
		this.labels = computed(() => this.labelsService.labels())();
	}

	ngAfterViewInit(): void {
		if (!this.headers || this.headers.length === 0) {
			throw new Error('Headers input is required and cannot be empty');
		}
	}

	protected getGridTemplateColumns() {
		let template = this.headers.map((header) => (header.colWidth ? header.colWidth : 'auto')).join(' ');
		if (this.enableSelecting) {
			template = 'min-content ' + template;
		}
		if (this.enableExpanding) {
			template = template + ' minmax(1rem, min-content)';
		}
		return template;
	}

	protected getGridColumnEnd() {
		//+1 é porque o grid column end espera uma unidade maior do que o tamanho da table
		//já que o valor não é inclusivo no dimensionamento da grid
		const columns = this.headers.length;
		return columns + (this.enableSelecting ? 1 : 0) + (this.enableExpanding ? 1 : 0) + 1;
	}

	protected handleSort(header: mspTableHeaderI) {
		if (!this.enableSorting) return;

		if (this.sort?.sortBy === header.name) {
			this.sort.sortOrder = this.sort.sortOrder === 'asc' ? 'desc' : 'asc';
		} else {
			this.sort = { sortBy: header.name, sortOrder: 'desc', sortDown: true };
		}

		this.getSort.emit({ sortBy: this.sort.sortBy, sortOrder: this.sort.sortOrder, sortDown: this.sort.sortOrder === 'desc' });
	}

	protected handleSearch(searchData: { id: string; values: any[] }) {
		this.searchData = { ...this.searchData, [searchData.id]: searchData };
		this.getSearch.emit(searchData);
	}

	protected toggleAllSelection() {
		if (!this.enableSelecting) return;
		this.selectedRowIndex = this.selectedRowIndex.length === this.data.length ? [] : this.data.map((_, index) => index);
		const selectedData = this.selectedRowIndex.map((index) => this.data[index]);
		this.getSelectedRow.emit(selectedData);
	}

	protected turnOnToggleSelection(rowIndex: number) {
		console.log(this.selectedRowIndex);
		if ((!this.enableSelecting && !this.enableLineSelection) || this.onLineClick) return;
		if (!this.selectedRowIndex.includes(rowIndex)) {
			console.log('toggle', this.selectedRowIndex);
			this.toggleSelection(rowIndex);
		}
	}

	protected toggleSelection(rowIndex: number) {
		if ((!this.enableSelecting && !this.enableLineSelection) || this.onLineClick) return;

		const index = this.selectedRowIndex.indexOf(rowIndex);
		if (index > -1) {
			this.selectedRowIndex.splice(index, 1);
		} else {
			this.selectedRowIndex.push(rowIndex);
		}
		const selectedData = this.selectedRowIndex.map((index) => this.data[index]);
		this.getSelectedRow.emit(selectedData);
	}

	public expandRow(rowIndex: number) {
		if (!this.enableExpanding) return;
		const index = this.expandedRowIndex.indexOf(rowIndex);
		if (index > -1) {
			this.expandedRowIndex.splice(index, 1);
		} else {
			this.expandedRowIndex.push(rowIndex);
		}
	}

	protected openContextMenu(e: MouseEvent, rowData?: any) {
		if (!this.enableContextMenu || !this.contextMenuOptions || this.contextMenuOptions.length === 0) return;
		e.preventDefault();
		setTimeout(() => {
			const data = this.selectedRowIndex.length > 0 ? this.selectedRowIndex.map((index) => this.data[index]) : [rowData];
			this.getContextMenuData.emit(data);
			this.contextMenuService.setContextMenuData(data);
			console.log('contextMenuOptions', [...this.contextMenuOptions]);
			this.contextMenuService.setContextMenuOptions(this.contextMenuOptions);
			this.contextMenuService.openContextMenu(e.clientX, e.clientY, 10);
		}, 1);
	}

	protected fCleanSearch() {
		for (const dropdown of this.sortDropdown) dropdown.deselectAll();
		this.searchData = {};
		this.cleanSearch.emit(null);
	}

	protected openOptionsContextMenu(e: MouseEvent) {
		e.preventDefault();
		this.contextMenuService.setContextMenuOptions(this.optionsContextMenuOptions);
		this.contextMenuService.openContextMenu(e.clientX - 96, e.clientY, 10);
	}

	checkEveryValueNotNullOrUndefined(values: any[]): boolean {
		return values.every((value) => value !== null && value !== undefined && value !== '');
	}

	protected separateCellMarkupFromValue(cellText: any, side: 'cellMarkup' | 'cellValue'): any {
		const markupRegex = /§([^§]+)§([^§]+)/;
		if (cellText == undefined || cellText == '') return side === 'cellMarkup' ? null : '';
		cellText = cellText.toString();
		const sCellText = cellText.split('§');
		if (side === 'cellValue') return sCellText.at(-1);
		if (side === 'cellMarkup' && markupRegex.test(cellText)) {
			const markupArray = sCellText[1].split(';');
			const styles: { [k: string]: string } = {};
			markupArray.forEach((markup: string) => {
				const markupValue = markup.split(':').slice(1).join(':');
				if (markup.startsWith('fc')) {
					const cssVariablePattern = /^var\(--[\w-]+\)$/;
					const hexColorPattern = /^#([\dA-Fa-f]{3}|[\dA-Fa-f]{6})$/;
					if (cssVariablePattern.test(markupValue) || hexColorPattern.test(markupValue)) {
						styles['color'] = markupValue;
					}
				}
				if (markup.startsWith('fs')) {
					if (markupValue === 'i') styles['font-style'] = 'italic';
					if (markupValue === 'b') styles['font-weight'] = 'bold';
				}
			});
			return styles;
		}
		return null;
	}

	protected handleCellValue(cellValue: any): string {
		return this.separateCellMarkupFromValue(cellValue, 'cellValue');
	}

	protected handleCellMarkup(cellValue: any): any {
		return this.separateCellMarkupFromValue(cellValue, 'cellMarkup');
	}
}
